import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { apiClient } from "../../shared/api/client";
import { PortalStyles } from "./PortalStyles";

/**
 * Kiểm chứng phiếu kết quả (#19137) — where the QR on a patient's result sheet leads. PUBLIC,
 * outside RequireAuth: whoever holds the paper (an employer, another clinic) is not a user.
 *
 * Shows only what the API returns — clinic, sheet code, when it was issued, signed or not, and the
 * SHA-256 of the issued file — never anything about the patient. The file check hashes the PDF in
 * the browser (it is never uploaded) and compares it with the stored hash: the same bytes the
 * clinic issued, or not.
 */
type Verification = {
  valid: boolean;
  clinicName: string | null;
  documentCode: string | null;
  issuedAt: string;
  signed: boolean;
  signedAt: string | null;
  sha256: string;
  signatureVerifiable: boolean;
};

function fmt(ts: string | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function VerifyResultPage() {
  const { code = "" } = useParams();
  const q = useQuery({
    queryKey: ["public-verify", code],
    queryFn: async (): Promise<Verification | null> => {
      try {
        return (await apiClient.get<Verification>(`/v1/public/verify/${encodeURIComponent(code)}`))
          .data;
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
      }
    },
    retry: false,
  });
  const [match, setMatch] = useState<null | boolean>(null);
  const [checking, setChecking] = useState(false);

  async function onFile(f: File | undefined) {
    if (!f || !q.data) return;
    setChecking(true);
    try {
      setMatch((await sha256Hex(f)) === q.data.sha256.toLowerCase());
    } finally {
      setChecking(false);
    }
  }

  const v = q.data;
  return (
    <div className="pp-shell">
      <PortalStyles />
      <div className="pp-center">
        <div className="pp-card pp-login-card" style={{ maxWidth: 520 }}>
          <div className="pp-brand">
            <h1 style={{ fontSize: 20, margin: "8px 0 4px" }}>Kiểm chứng phiếu kết quả</h1>
            <p className="pp-note">Mã kiểm chứng: {code}</p>
          </div>

          {q.isLoading && <p className="pp-note">Đang kiểm tra…</p>}
          {q.isError && (
            <p className="pp-error">Không kiểm tra được lúc này. Vui lòng thử lại sau ít phút.</p>
          )}
          {q.isSuccess && !v && (
            <p className="pp-error">
              Không tìm thấy phiếu với mã này. Phiếu có thể không do Addy HIS phát hành, hoặc mã đã
              bị chép sai.
            </p>
          )}
          {v && (
            <>
              <p style={{ margin: "0 0 14px" }}>
                <span className="pp-badge ok">Phiếu do phòng khám phát hành</span>
              </p>
              <div className="pp-grid">
                <div className="pp-kv">
                  <div className="k">Phòng khám</div>
                  <div className="v">{v.clinicName ?? "—"}</div>
                </div>
                <div className="pp-kv">
                  <div className="k">Mã phiếu</div>
                  <div className="v">{v.documentCode ?? "—"}</div>
                </div>
                <div className="pp-kv">
                  <div className="k">Phát hành lúc</div>
                  <div className="v">{fmt(v.issuedAt)}</div>
                </div>
                <div className="pp-kv">
                  <div className="k">Chữ ký</div>
                  <div className="v">
                    {v.signed ? `Bác sĩ đã ký lúc ${fmt(v.signedAt)}` : "Chưa ký số"}
                  </div>
                </div>
              </div>
              {!v.signatureVerifiable && (
                <p className="pp-note" style={{ marginTop: 12 }}>
                  Trang này xác nhận phiếu do phòng khám phát hành qua Addy HIS và nội dung tệp chưa
                  bị sửa. Đây chưa phải chữ ký số có giá trị pháp lý.
                </p>
              )}

              <div style={{ marginTop: 18 }}>
                <div className="pp-label" style={{ fontWeight: 600, fontSize: 14 }}>
                  Đối chiếu tệp PDF đang có
                </div>
                <p className="pp-note">
                  Chọn tệp phiếu kết quả để so với bản phòng khám đã phát. Tệp chỉ được đọc trên máy
                  của bạn, không gửi đi đâu.
                </p>
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ marginTop: 8 }}
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
                {checking && <p className="pp-note">Đang đối chiếu…</p>}
                {match === true && (
                  <p style={{ marginTop: 10 }}>
                    <span className="pp-badge ok">Khớp — đúng tệp phòng khám đã phát</span>
                  </p>
                )}
                {match === false && (
                  <p className="pp-error">
                    Không khớp — tệp này khác với bản phòng khám đã phát (có thể đã bị sửa, hoặc là
                    phiếu khác).
                  </p>
                )}
                <p className="pp-note" style={{ wordBreak: "break-all", marginTop: 10 }}>
                  SHA-256: {v.sha256}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
